import { useState } from "react";
import type { Cell, Confidence } from "@/types";
import { resolveSources } from "@/data";
import { confLabel, scoreColor } from "@/format";

export function ConfBadge({ conf }: { conf: Confidence }) {
  return <span className={`badge ${conf}`}>{confLabel[conf]}</span>;
}

export function StatusDot({ status }: { status: string | null }) {
  const cls = status === "active" ? "status-active" : status === "defunct" ? "status-defunct" : "status-unknown";
  return <span className={`status-dot ${cls}`} title={status ?? "unknown"} />;
}

export function Avatar({ text }: { text: string }) {
  return <span className="avatar">{text}</span>;
}

/** Renders source citations as a small popover. */
export function SourceRef({ ids }: { ids: string[] }) {
  const [open, setOpen] = useState(false);
  if (!ids || ids.length === 0) return null;
  const sources = resolveSources(ids);
  return (
    <span className="pop">
      <span
        className="pop-trigger tiny faint"
        onClick={() => setOpen((o) => !o)}
        onMouseEnter={() => setOpen(true)}
        onMouseLeave={() => setOpen(false)}
        role="button"
        tabIndex={0}
        aria-label="View sources"
      >
        ⓘ {ids.length} src
      </span>
      {open && (
        <span className="pop-body" onMouseEnter={() => setOpen(true)} onMouseLeave={() => setOpen(false)}>
          {sources.map((s) => (
            <span key={s.id} style={{ display: "block", marginBottom: 6 }}>
              <strong>{s.title}</strong>
              <br />
              <span className="tiny">
                {s.kind}
                {s.url ? (
                  <>
                    {" · "}
                    <a href={s.url} target="_blank" rel="noreferrer noopener">
                      link
                    </a>
                  </>
                ) : (
                  " · no public URL"
                )}
                {s.accessedAt ? ` · ${s.accessedAt}` : ""}
              </span>
              {s.note && <span className="tiny faint" style={{ display: "block" }}>{s.note}</span>}
            </span>
          ))}
        </span>
      )}
    </span>
  );
}

/** Display a Cell value with confidence + sources; shows "Unknown" explicitly. */
export function CellValue<T>({
  cell,
  render,
  showSrc = true,
}: {
  cell: Cell<T> | undefined;
  render?: (v: T) => React.ReactNode;
  showSrc?: boolean;
}) {
  if (!cell || cell.v === null) {
    return <span className="faint" title={cell?.note}>Unknown</span>;
  }
  const content = render ? render(cell.v) : String(cell.v);
  return (
    <span className="row" style={{ gap: 6, display: "inline-flex", flexWrap: "wrap" }}>
      <span style={{ overflowWrap: "anywhere", minWidth: 0 }}>{content}</span>
      <ConfBadge conf={cell.conf} />
      {showSrc && <SourceRef ids={cell.src} />}
      {cell.note && <span className="pop"><span className="pop-trigger tiny faint" title={cell.note}>note</span></span>}
    </span>
  );
}

export function ScoreBar({ score, width = 90 }: { score: number | null; width?: number }) {
  const v = score ?? 0;
  return (
    <span className="row" style={{ gap: 8 }}>
      <span className="scorebar" style={{ width }}>
        <span style={{ width: `${v}%`, background: scoreColor(score) }} />
      </span>
      <span className="score-num" style={{ color: scoreColor(score), minWidth: 34, textAlign: "right" }}>
        {score === null ? "—" : score.toFixed(1)}
      </span>
    </span>
  );
}

export function Coverage({ value }: { value: number }) {
  return (
    <span className="row tiny faint" style={{ gap: 6 }} title="Fraction of scoring weight backed by known data">
      <span className="cov" style={{ width: 44 }}>
        <span style={{ width: `${Math.round(value * 100)}%` }} />
      </span>
      {Math.round(value * 100)}%
    </span>
  );
}

export function ModeledBadge() {
  return (
    <span className="badge modeled" title="Representative structural model, not a verified live quote">
      modeled
    </span>
  );
}

export function Banner({ children }: { children: React.ReactNode }) {
  return <div className="banner">{children}</div>;
}
